import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { Slider } from '@/components/ui/slider';
import { Calculator, CalendarClock, Wallet, AlertTriangle, Info, Send } from 'lucide-react';
import { simulateLoan, isFinanceEligible, FINANCING } from '@/utils/financing';

const fmtMoney = (v) =>
  `${Math.round(Number(v) || 0).toLocaleString('fr-BE')} €`;

const NotEligible = ({ reason, carLabel }) => {
  const { t } = useTranslation();
  const descKey =
    reason === 'age' ? 'financing.notEligibleAgeDesc' : 'financing.notEligiblePriceDesc';
  const subject = t('financing.requestSubject');
  const message = t('financing.requestBody', {
    car: carLabel || '—',
    amount: '—',
    months: '—',
    monthlyMin: '—',
    monthlyMax: '—',
  });
  const href = `/contact?subject=${encodeURIComponent(subject)}&message=${encodeURIComponent(message)}`;
  return (
    <div
      className="rounded-2xl border border-amber-200 bg-amber-50 p-5"
      data-testid="financing-not-eligible"
    >
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center flex-shrink-0">
          <AlertTriangle size={16} />
        </div>
        <div>
          <p className="text-sm font-bold text-amber-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
            {t('financing.notEligibleTitle')}
          </p>
          <p className="text-xs text-amber-800 mt-1 leading-relaxed">{t(descKey)}</p>
          <Link
            to={href}
            className="mt-3 inline-flex items-center text-xs font-semibold text-amber-900 underline underline-offset-4"
            data-testid="financing-contact-link"
          >
            {t('financing.requestQuote')}
          </Link>
        </div>
      </div>
    </div>
  );
};

const FinancingCalculator = ({ price, year, make, model, className = '', variant = 'sidebar' }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const carLabel = [year, make, model].filter(Boolean).join(' ').trim();
  const eligibility = isFinanceEligible({ price, year });
  const initialLoan = Math.max(
    FINANCING.MIN_LOAN,
    Math.min(FINANCING.MAX_LOAN, Math.round(Number(price) || FINANCING.MIN_LOAN))
  );

  const [loan, setLoan] = useState(initialLoan);
  const [duration, setDuration] = useState(FINANCING.DEFAULT_DURATION);

  const sim = useMemo(() => simulateLoan(loan, duration), [loan, duration]);

  if (!price) return null;
  if (!eligibility.eligible) {
    return (
      <div className={className} data-testid="financing-calculator">
        <NotEligible reason={eligibility.reason} carLabel={carLabel} />
      </div>
    );
  }

  const compact = variant === 'sidebar';

  return (
    <div className={className} data-testid="financing-calculator">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-lg bg-blue-50 border border-blue-100 flex items-center justify-center text-blue-900 flex-shrink-0">
            <Calculator size={16} />
          </div>
          <div>
            <span className="text-[10px] uppercase tracking-wider text-emerald-600 font-bold">
              {t('financing.eyebrow')}
            </span>
            <h3
              className={`font-bold text-slate-900 leading-tight ${compact ? 'text-base' : 'text-xl sm:text-2xl'}`}
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              {t('financing.title')}
            </h3>
          </div>
        </div>
      </div>

      {/* Sliders */}
      <div className="space-y-4 mb-5">
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-3.5">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-1.5 text-slate-600 text-xs font-medium">
              <Wallet size={13} className="text-blue-900" />
              {t('financing.loanAmount')}
            </div>
            <span className="text-slate-900 text-sm font-bold" data-testid="financing-loan-value">
              {fmtMoney(sim.loan)}
            </span>
          </div>
          <Slider
            value={[sim.loan]}
            min={FINANCING.MIN_LOAN}
            max={FINANCING.MAX_LOAN}
            step={FINANCING.LOAN_STEP}
            onValueChange={(v) => setLoan(v[0])}
            data-testid="financing-slider-loan"
          />
        </div>

        <div className="bg-slate-50 border border-slate-200 rounded-xl p-3.5">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-1.5 text-slate-600 text-xs font-medium">
              <CalendarClock size={13} className="text-blue-900" />
              {t('financing.duration')}
            </div>
            <span className="text-slate-900 text-sm font-bold" data-testid="financing-duration-value">
              {sim.months} {t('financing.months')}
            </span>
          </div>
          <Slider
            value={[sim.months]}
            min={FINANCING.MIN_DURATION}
            max={FINANCING.MAX_DURATION}
            step={6}
            onValueChange={(v) => setDuration(v[0])}
            data-testid="financing-slider-duration"
          />
        </div>
      </div>

      {/* Monthly result — headline */}
      <div className="bg-blue-900 text-white rounded-xl p-4 mb-3" data-testid="financing-result-monthly">
        <p className="text-[10px] uppercase tracking-wider text-blue-200 font-semibold mb-1">
          {t('financing.monthlyRange')}
        </p>
        <p className="text-2xl font-extrabold leading-none" style={{ fontFamily: 'Outfit, sans-serif' }}>
          {fmtMoney(sim.monthlyMin)}
          <span className="text-blue-200 mx-1.5 text-lg">→</span>
          {fmtMoney(sim.monthlyMax)}
        </p>
        <p className="text-blue-200 text-xs mt-1">{t('financing.perMonth')} · {t('financing.aprRange')}</p>
      </div>

      {/* Total secondary */}
      <div className="grid grid-cols-2 gap-2 mb-4">
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-3" data-testid="financing-result-total">
          <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">
            {t('financing.totalRange')}
          </p>
          <p className="text-slate-900 text-sm font-bold leading-tight">
            {fmtMoney(sim.totalMin)}
            <span className="text-slate-400 mx-1">→</span>
            {fmtMoney(sim.totalMax)}
          </p>
        </div>
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-3" data-testid="financing-result-cost">
          <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-1">
            {t('financing.cost')}
          </p>
          <p className="text-slate-900 text-sm font-bold leading-tight">
            {fmtMoney(sim.totalMin - sim.loan)}
            <span className="text-slate-400 mx-1">→</span>
            {fmtMoney(sim.totalMax - sim.loan)}
          </p>
        </div>
      </div>

      {/* CTA — Request financing offer with pre-filled context */}
      <button
        type="button"
        onClick={() => {
          const subject = t('financing.requestSubject');
          const message = t('financing.requestBody', {
            car: carLabel || '—',
            amount: Math.round(sim.loan).toLocaleString('fr-BE'),
            months: sim.months,
            monthlyMin: Math.round(sim.monthlyMin).toLocaleString('fr-BE'),
            monthlyMax: Math.round(sim.monthlyMax).toLocaleString('fr-BE'),
          });
          navigate(`/contact?subject=${encodeURIComponent(subject)}&message=${encodeURIComponent(message)}`);
        }}
        className="w-full inline-flex items-center justify-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold rounded-xl px-4 py-3 transition-colors mb-3"
        data-testid="financing-request-cta"
      >
        <Send size={14} />
        {t('financing.requestCta')}
      </button>

      {/* Mandatory Belgian legal warning */}
      <div
        className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 mb-3"
        data-testid="financing-legal-warning"
      >
        <AlertTriangle size={13} className="text-amber-600 flex-shrink-0 mt-0.5" />
        <p className="text-[11px] leading-snug text-amber-900 font-semibold">
          {t('financing.warning')}
        </p>
      </div>

      {/* Disclaimer */}
      <div className="flex items-start gap-2 text-[11px] text-slate-500 leading-relaxed">
        <Info size={12} className="text-slate-400 flex-shrink-0 mt-0.5" />
        <p>{t('financing.disclaimer')}</p>
      </div>
    </div>
  );
};

export default FinancingCalculator;
